import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS } from "@/lib/content";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-kryvo-green/10 bg-white/85 shadow-[0_8px_30px_-18px_rgba(6,78,42,0.35)] backdrop-blur-xl" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="container-x flex h-[72px] items-center justify-between" aria-label="Main navigation">
        <Link to="/" data-testid="nav-logo-link" aria-label="Kryvo Technologies — home">
          <Logo />
        </Link>

        <ul className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-kryvo-mist text-kryvo-green" : "text-kryvo-ink/70 hover:text-kryvo-deep"
                  }`
                }
              >
                {link.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="group hidden items-center gap-2 rounded-full bg-kryvo-green px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-kryvo-deep sm:inline-flex"
          >
            Let's Talk
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-kryvo-green/20 bg-white text-kryvo-deep lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed inset-x-0 top-[72px] bottom-0 z-40 overflow-y-auto bg-white/98 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <ul className="container-x flex flex-col gap-2 py-8">
              {NAV_LINKS.map((link, i) => (
                <motion.li
                  key={link.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.3 }}
                >
                  <NavLink
                    to={link.to}
                    end={link.to === "/"}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-4 font-display text-2xl font-semibold transition-colors ${
                        isActive ? "bg-kryvo-mist text-kryvo-green" : "text-kryvo-ink hover:bg-kryvo-mist"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                  {link.to === "/staffing" && (
                    <Link
                      to="/staffing"
                      data-testid="mobile-nav-link-offshore-staffing"
                      className="mt-1 flex items-center gap-2 rounded-xl px-4 py-2 pl-8 text-sm font-medium text-kryvo-sub transition-colors hover:text-kryvo-green"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-kryvo-lime" aria-hidden="true" />
                      Offshore Staffing Services
                    </Link>
                  )}
                </motion.li>
              ))}
              <motion.li
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className="pt-4"
              >
                <Link
                  to="/contact"
                  data-testid="mobile-nav-cta"
                  className="flex items-center justify-center gap-2 rounded-full bg-kryvo-green px-6 py-4 text-base font-semibold text-white"
                >
                  Let's Talk
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </Link>
              </motion.li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
